import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Card, message, Tag, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { approvalApi, PendingItem } from '../../services/approvalApi';

const formTypeLabels: Record<string, string> = {
  SC: '銷售合約',
  PO: '採購單',
  DO: '交運單',
  J214: '研發採購單',
  D202: '繳庫單',
};

const formTypeColors: Record<string, string> = {
  SC: 'blue',
  PO: 'orange',
  DO: 'purple',
  J214: 'green',
  D202: 'cyan',
};

const columns: ColumnsType<PendingItem> = [
  {
    title: '表單類型',
    dataIndex: 'form_type',
    key: 'form_type',
    width: 120,
    render: (val: string) => (
      <Tag color={formTypeColors[val] || 'default'}>
        {formTypeLabels[val] || val}
      </Tag>
    ),
  },
  {
    title: '單號',
    dataIndex: 'record_no',
    key: 'record_no',
    width: 140,
  },
  {
    title: '送審人',
    dataIndex: 'submitter',
    key: 'submitter',
    width: 120,
  },
  {
    title: '送審時間',
    dataIndex: 'submit_date',
    key: 'submit_date',
    width: 140,
  },
  {
    title: '摘要',
    dataIndex: 'summary',
    key: 'summary',
    ellipsis: true,
  },
];

const ApprovalList: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(false);

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const data = await approvalApi.getPending();
      setItems(data);
    } catch {
      message.error('載入待審核清單失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  /** 雙擊跳轉到對應表單 */
  const handleRowDoubleClick = useCallback(
    (record: PendingItem) => {
      switch (record.form_type) {
        case 'SC':
          navigate(`/sc?review=${encodeURIComponent(record.record_no)}`);
          break;
        case 'PO':
          navigate(`/d201?review=${encodeURIComponent(record.record_no)}`);
          break;
        case 'DO':
          navigate(`/c204?review=${encodeURIComponent(record.record_no)}`);
          break;
        case 'J214':
          navigate(`/j214?review=${encodeURIComponent(record.record_no)}`);
          break;
        case 'D202':
          navigate(`/d202?review=${encodeURIComponent(record.record_no)}`);
          break;
        default:
          message.info(`尚不支援表單類型: ${record.form_type}`);
      }
    },
    [navigate],
  );

  return (
    <div style={{ padding: 24 }}>
      <Card title="待審核清單" bordered={false}>
        <Alert
          message="雙擊項目可跳轉至對應表單進行審核"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Table<PendingItem>
          rowKey={(r) => `${r.form_type}_${r.record_no}`}
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 筆` }}
          onRow={(record) => ({
            onDoubleClick: () => handleRowDoubleClick(record),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: '目前沒有待審核項目' }}
        />
      </Card>
    </div>
  );
};

export default ApprovalList;
